// AppScrollRow — a row that scrolls sideways and FADES only an edge that hides
// content (overflow law §3b.5, DESIGN-WL-D2-KIT-OVERFLOW-LAW-1). ONE kit piece
// for every scrolling row, so the fade cannot drift between copies:
// AppSegmented's track, the header's two tab strips (Tabs, MobileTabs), the
// composer's recent-board chips.
//
//   • THE AXIS IS THE ONE THAT HIDES CONTENT, never a fixed one
//     (`edgeFadeAxis`): sideways by default, down the column for a consumer
//     that turns the row on its end — the header's tab strip on a left or
//     right rail (`data-nav-rail`: `flexDirection: column`, `overflowY:
//     auto`). Every edge the kit reads — the flags, the mask and the reveal —
//     follows that axis. It used to read `scrollWidth` / `scrollLeft` only,
//     so a COLUMN raised no flag, painted no fade and never revealed the
//     current child: measured live at 1194 x 834 (iPad 11 landscape, dev
//     /w/dfli/dfi), the rail's 298px lane hid 64px of its 362px of tabs with
//     no hint, and opening one of them showed no active tab at all
//     (DESIGN-SHELL-RAIL-TRAILING-CLUSTER-COVERS-TABS-1). The flags name the
//     axis they describe (`data-fade-axis="y"`, absent sideways), and the
//     column's mask rules carry one attribute more than the row's, so no
//     class-insertion order ever decides which gradient paints.
//   • No scrollbar chrome; it shrinks inside a flex parent (min-width 0) and
//     its overscroll never chains to the page, on either axis.
//   • THE FADE: `data-fade-start` / `data-fade-end` + a `C`-band mask. A mask
//     paints nothing new — it only scales the alpha of what is there — and a
//     side with nothing beyond it paints no fade at all.
//   • THE FLAGS come from the FIRST and LAST visible child against the row's
//     visible box, never from the raw scroll offset: a row with its own
//     padding rests scrolled by that padding once scroll-snap aligns its first
//     child (3.68 px on CRM at 390 px, dev bad4186b3) and hides nothing —
//     the offset rule faded the start over the chosen first segment. They are
//     written through the element's own update (attr) only when one flips,
//     never on the node and never into a state: a row may sit where the
//     NEAREST state belongs to someone else (AppSegmented inside a form's
//     options contract), so the row owns none.
//   • THE GEOMETRY comes from an IntersectionObserver on the row's own
//     children (root = the row): the browser computes it inside its frame,
//     so the row reads NO box during render or update — the
//     getBoundingClientRect reads it did on every onContentUpdate were forced
//     layouts (~320 ms over six calls per staging boot, 2026-10-10). Each
//     child keeps its last entry and the row's scrollLeft when it arrived.
//   • THE CUT RULE (as before): a side is cut when MORE than 1 px of the
//     child lies past that edge of the row's box (the entry's own box against
//     the entry's root box). The thresholds (0, 0.99, 1) only pace delivery.
//     A display:none child reports a zero box and takes no part.
//   • RE-OBSERVED (a fresh entry per child, next frame) on mount, on every
//     resize (its own ResizeObserver) and when its children change
//     (onContentUpdate: a chip, a tab, a view added without resizing the
//     row). ON SCROLL the row re-reads the stored entries moved by the scroll
//     since they arrived (arithmetic, no box read): a child wider than the
//     row (HomeAppStrip's one dock) crosses no threshold while it scrolls, so
//     the observer alone would never report it. Both observers go in
//     onRemove.
//   • TIMING: the observer reports after the browser paints a frame. A row
//     that overflows on mount paints its first frame without fades (and an
//     unrevealed current child); the flags arrive with the observer's first
//     callback. A hidden tab runs no frame, so its rows wait for the first
//     visible frame.
//   • THE CURRENT CHILD (aria-current, or aria-selected / aria-checked /
//     aria-pressed = "true" — a tab, a radio, a toggle chip) stays whole: on
//     mount, resize or a content change a current child the row cuts moves
//     to the row's middle (from its observer entry, flags in the same
//     callback); a child that
//     BECOMES current calls `el.parent.scope.edgeFadeBecameCurrent(el.parent,
//     el.node)`. A child already in full view is never moved, so the row
//     never fights a user who scrolled it. A row vetoes reveals (a drag in
//     progress) by declaring its own `scope.edgeFadeCanReveal(el)`.
//
// USE — layout (display / flow / gap / align / padding) is the consumer's:
//   Chips: { extends: 'AppScrollRow', flow: 'x', gap: 'Z', children: … }
// or spread `...AppScrollRow` into a definition that extends something else
// (AppSegmented), merging `scope` when it declares its own.
export const AppScrollRow = {
  minWidth: '0',
  overflowX: 'auto',
  overflowY: 'hidden',
  overscrollBehaviorX: 'contain',
  // A consumer that turns the row on its end scrolls the OTHER axis, and its
  // overscroll must not chain to the page either (the rail's tab column).
  overscrollBehaviorY: 'contain',
  scrollbarWidth: 'none',
  '::-webkit-scrollbar': { display: 'none' },

  '&[data-fade-start]': {
    maskImage: 'linear-gradient(to right, transparent, black var(--spacing-C))'
  },
  '&[data-fade-end]': {
    maskImage: 'linear-gradient(to right, black calc(100% - var(--spacing-C)), transparent)'
  },
  '&[data-fade-start][data-fade-end]': {
    maskImage:
      'linear-gradient(to right, transparent, black var(--spacing-C), black calc(100% - var(--spacing-C)), transparent)'
  },
  // THE COLUMN'S OWN BAND (the axis note above): one attribute more than the
  // matching row rule, so the column always wins where it applies and no
  // insertion order is ever load-bearing.
  '&[data-fade-axis="y"][data-fade-start]': {
    maskImage: 'linear-gradient(to bottom, transparent, black var(--spacing-C))'
  },
  '&[data-fade-axis="y"][data-fade-end]': {
    maskImage: 'linear-gradient(to bottom, black calc(100% - var(--spacing-C)), transparent)'
  },
  '&[data-fade-axis="y"][data-fade-start][data-fade-end]': {
    maskImage:
      'linear-gradient(to bottom, transparent, black var(--spacing-C), black calc(100% - var(--spacing-C)), transparent)'
  },

  scope: {
    // The children that take room — a display:none child reports a zero box
    // to the observer. A child with no entry yet (just added) counts.
    edgeFadeChildren(el) {
      const n = el && el.node
      if (!n) return []
      const seen = el._edgeFadeSeen
      return Array.from(n.children || []).filter((c) => {
        if (!c) return false
        const s = seen && seen.get(c)
        const r = s && s.entry && s.entry.boundingClientRect
        if (!r) return true
        return r.width > 0 || r.height > 0
      })
    },

    // WHICH AXIS SCROLLS — the one that hides content, so the row needs no
    // flag of its own and a consumer that flips it (the rail's column) needs
    // no second kit piece. Sideways unless the other axis hides more: a row
    // declares `overflowY: hidden` and a column `overflowX: hidden`, so only
    // one of the two is ever non-zero in practice.
    edgeFadeAxis(el) {
      const n = el && el.node
      if (!n) return 'x'
      const down = (n.scrollHeight || 0) - (n.clientHeight || 0)
      const across = (n.scrollWidth || 0) - (n.clientWidth || 0)
      return down > 1 && down > across ? 'y' : 'x'
    },

    // How much this axis hides, and where it rests now.
    edgeFadeHidden(el, axis) {
      const n = el && el.node
      if (!n) return 0
      return axis === 'y'
        ? (n.scrollHeight || 0) - (n.clientHeight || 0)
        : (n.scrollWidth || 0) - (n.clientWidth || 0)
    },

    edgeFadeAt(el, axis) {
      const n = el && el.node
      if (!n) return 0
      return (axis || this.edgeFadeAxis(el)) === 'y' ? n.scrollTop || 0 : n.scrollLeft || 0
    },

    // How far a child lies past each edge of the row's box, from its stored
    // entry moved by the scroll since it arrived (`now` = the row's own
    // offset on `axis`). Null when the entry carries no boxes.
    edgeFadeOverhang(seen, now = 0, axis = 'x') {
      const e = seen && seen.entry
      const r = e && e.boundingClientRect
      const root = e && e.rootBounds
      if (!r || !root) return null
      const shift = (now || 0) - (seen.at || 0)
      if (axis === 'y')
        return { start: root.top - (r.top - shift), end: r.bottom - shift - root.bottom }
      return { start: root.left - (r.left - shift), end: r.right - shift - root.right }
    },

    // Is this child cut on that side? More than 1 px of it past that edge
    // (sub-pixel slack for a fractional layout). Takes an entry, or a stored
    // { entry, at } with the row's own offset now.
    edgeFadeCut(entry, side, now = 0, axis = 'x') {
      if (!entry) return false
      const seen = entry.entry ? entry : { entry, at: now }
      const o = this.edgeFadeOverhang(seen, now, axis)
      if (!o) return seen.entry.intersectionRatio < 0.99
      return o[side] > 1
    },

    // Which edges hide a child, from the stored entries of the first and the
    // last child that take room, moved by the scroll since they arrived.
    edgeFadeSync(el) {
      const n = el && el.node
      if (!n || typeof el.update !== 'function') return
      let start = false
      let end = false
      const axis = this.edgeFadeAxis(el)
      const seen = el._edgeFadeSeen
      if (seen && this.edgeFadeHidden(el, axis) > 1) {
        const kids = this.edgeFadeChildren(el).filter((c) => seen.has(c))
        if (kids.length) {
          const now = this.edgeFadeAt(el, axis)
          start = this.edgeFadeCut(seen.get(kids[0]), 'start', now, axis)
          end = this.edgeFadeCut(seen.get(kids[kids.length - 1]), 'end', now, axis)
        }
      }
      if (!!el._fadeStart === start && !!el._fadeEnd === end && el._fadeAxis === axis) return
      el._fadeStart = start
      el._fadeEnd = end
      el._fadeAxis = axis
      el.update({
        attr: {
          'data-fade-start': start ? '' : null,
          'data-fade-end': end ? '' : null,
          'data-fade-axis': axis === 'y' ? 'y' : null
        }
      })
    },

    // A row may veto reveals (a drag reads child positions under the pointer).
    edgeFadeCanReveal() {
      return true
    },

    // The current child, as the DOM states it.
    edgeFadeCurrent(el) {
      return (
        this.edgeFadeChildren(el).find((c) => {
          const cur = c.getAttribute && c.getAttribute('aria-current')
          return (
            (cur && cur !== 'false') ||
            (c.getAttribute && c.getAttribute('aria-selected') === 'true') ||
            (c.getAttribute && c.getAttribute('aria-checked') === 'true') ||
            (c.getAttribute && c.getAttribute('aria-pressed') === 'true')
          )
        }) || null
      )
    },

    // Bring one child node to the row's middle — only if the row cuts it.
    // Reads the child's stored entry, never its box. On a column the middle
    // is the lane's own, down the axis that scrolls.
    edgeFadeReveal(el, node) {
      const n = el && el.node
      const s = node && el._edgeFadeSeen && el._edgeFadeSeen.get(node)
      if (!n || !s || !this.edgeFadeCanReveal(el)) return
      const r = s.entry.boundingClientRect
      const box = s.entry.rootBounds
      const axis = this.edgeFadeAxis(el)
      const near = axis === 'y' ? 'top' : 'left'
      const size = axis === 'y' ? 'height' : 'width'
      if (!r || !box || !box[size] || !(r.width > 0 || r.height > 0)) return
      const from = this.edgeFadeAt(el, axis)
      const o = this.edgeFadeOverhang(s, from, axis)
      if (!o || (o.start <= 1 && o.end <= 1)) return
      const hidden = this.edgeFadeHidden(el, axis)
      if (hidden <= 1 || typeof n.scrollTo !== 'function') return
      const shift = from - (s.at || 0)
      const target = from + (r[near] - shift + r[size] / 2 - (box[near] + box[size] / 2))
      const to = Math.max(0, Math.min(hidden, target))
      if (Math.abs(to - from) < 0.5) return
      n.scrollTo(axis === 'y' ? { top: to } : { left: to })
    },

    // The observer's callback: store each child's entry with the row's own
    // offset now, reveal a pending current child, then read the flags —
    // in the same callback, the entries moved by the reveal's scroll.
    edgeFadeSeen(el, entries) {
      const n = el && el.node
      if (!n) return
      const seen = el._edgeFadeSeen || (el._edgeFadeSeen = new Map())
      const at = this.edgeFadeAt(el)
      for (const e of entries || []) if (e && e.target) seen.set(e.target, { entry: e, at })
      for (const c of Array.from(seen.keys())) if (c.parentNode !== n) seen.delete(c)
      const want = el._edgeFadeWant
      if (want) {
        const node = want === true ? this.edgeFadeCurrent(el) : want
        if (!node || node.parentNode !== n || seen.has(node)) {
          el._edgeFadeWant = null
          if (node && seen.has(node)) this.edgeFadeReveal(el, node)
        }
      }
      this.edgeFadeSync(el)
    },

    // A scroll: the stored entries, moved by it, give the flags. A child
    // wider than the row crosses no threshold, so no entry would come.
    edgeFadeScrolled(el) {
      if (el && el._edgeFadeSeen) this.edgeFadeSync(el)
    },

    // Watch every child afresh: the observer queues one entry per child for
    // its next frame. No box is read here.
    edgeFadeObserve(el) {
      const n = el && el.node
      const io = el && el._edgeFadeIo
      if (!n || !io) return
      io.disconnect()
      for (const c of Array.from(n.children || [])) if (c) io.observe(c)
    },

    // A child just became current (a click, the keyboard, a route, a value
    // set from outside): show it whole on the observer's next frame, with
    // the flags.
    edgeFadeBecameCurrent(el, node) {
      if (!el) return
      el._edgeFadeWant = node || true
      this.edgeFadeObserve(el)
    },

    // The row changed shape: reveal the current child first (it moves the
    // scroll), then read the flags against the settled position.
    edgeFadeChanged(el) {
      if (!el) return
      el._edgeFadeWant = true
      this.edgeFadeObserve(el)
    },

    edgeFadeArm(el) {
      const n = el && el.node
      if (!n) return
      if (!el._edgeFadeIo && typeof IntersectionObserver !== 'undefined') {
        el._edgeFadeIo = new IntersectionObserver(
          (entries) => {
            if (el._edgeFadeIo) this.edgeFadeSeen(el, entries)
          },
          { root: n, threshold: [0, 0.99, 1] }
        )
      }
      if (!el._edgeFadeRo && typeof ResizeObserver !== 'undefined') {
        el._edgeFadeRo = new ResizeObserver(() => this.edgeFadeChanged(el))
        el._edgeFadeRo.observe(n)
      }
      this.edgeFadeChanged(el)
    },

    edgeFadeDisarm(el) {
      if (el._edgeFadeIo) el._edgeFadeIo.disconnect()
      if (el._edgeFadeRo) el._edgeFadeRo.disconnect()
      el._edgeFadeIo = null
      el._edgeFadeRo = null
      el._edgeFadeSeen = null
      el._edgeFadeWant = null
    }
  },

  onScroll: (e, el) => el.scope.edgeFadeScrolled(el),
  onMounted: (el) => el.scope.edgeFadeArm(el),
  onContentUpdate: (el) => el.scope.edgeFadeChanged(el),
  onRemove: (el) => el.scope.edgeFadeDisarm(el)
}
